/**
 * Deep-links from a selected element to the backend's trace UI ("show me the
 * traces behind this"). Pure URL builders + a small context the host (Studio)
 * fills from the configured backend; the telemetry sections render a link when
 * one is available. Absent provider / unbuildable → no link (composable).
 *
 * Jaeger's query endpoint doubles as its UI (all-in-one serves /search), so we
 * build real search URLs. Tempo's query API is not a UI (it needs Grafana), so
 * Tempo links are omitted until a UI base is configured.
 */
import { createContext, useContext } from "react";

export type TraceBackendKind = "jaeger" | "tempo";

/** Jaeger UI search for a service over a lookback window. */
export function jaegerSearchUrl(endpoint: string, service: string, lookback = "1h"): string {
  const base = endpoint.replace(/\/+$/, "");
  const q = new URLSearchParams({ service, lookback, limit: "20" });
  return `${base}/search?${q.toString()}`;
}

/** Build the node/edge trace-UI linkers for a backend, or null when the backend
 *  has no reachable UI from its query endpoint (e.g. Tempo without Grafana). */
export function buildTraceLinks(
  kind: TraceBackendKind,
  endpoint: string | null | undefined,
): TraceLinks | null {
  if (!endpoint) return null;
  if (kind === "jaeger") {
    return {
      nodeUrl: (service) => jaegerSearchUrl(endpoint, service),
      // Edge → search the CALLEE service (traces reaching the downstream).
      edgeUrl: (_from, to) => jaegerSearchUrl(endpoint, to),
      label: "Jaeger",
    };
  }
  // Tempo query endpoint is not a UI — no deep-link until a Grafana base exists.
  return null;
}

export interface TraceLinks {
  nodeUrl(service: string): string | null;
  edgeUrl(from: string, to: string): string | null;
  /** Backend name for the link label ("View in Jaeger ↗"). */
  label: string;
}

const Ctx = createContext<TraceLinks | null>(null);
export const TraceLinkProvider = Ctx.Provider;

/** The host-provided trace linkers, or null when none are mounted/buildable. */
export function useTraceLinks(): TraceLinks | null {
  return useContext(Ctx);
}
